/* eslint-disable indent */
import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import jwtDecode from 'jwt-decode'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import authApi from 'api/auth'
import { codifyAPIError, isErrorCode } from 'utils/helpers'
import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import { LOGIN, LOGOUT } from 'reducers/user.js'
import { challengeTypes, mfaTypes } from './types.js'

/** @typedef {import('./types.js').CognitoState} CognitoState */
/** @typedef {import('./types.js').CognitoDispatch} CognitoDispatch */

export const actions = Object.freeze({
  loading: 'loading',
  error: 'error',
  setUser: 'setUser',
  setChallenge: 'setChallenge',
  setMFASetupChallenge: 'setMFASetupChallenge',
  resetChallenge: 'resetChallenge',
  setMFASettings: 'setMFASettings',
  authenticateAsync: 'authenticateAsync',
  handleChangePasswordChallengeAsync: 'handleChangePasswordChallengeAsync',
  handleMFAChallengeAsync: 'handleMFAChallengeAsync',
  getMFASettingsAsync: 'getMFASettingsAsync',
  updateMFASettingsAsync: 'updateMFASettingsAsync',
  setupMFATOTPAsync: 'setupMFATOTPAsync',
  setupEmailOTPAsync: 'setupEmailOTPAsync',
})

/** @type {React.Context<CognitoState>} */
export const CognitoContext = React.createContext()
/** @type {React.Context<CognitoDispatch>} */
export const CognitoDispatchContext = React.createContext()

export const OTP_LENGTH = 6
export const REMEMBERED_DEVICE_KEY = 'fd:rememberedDevice'
const MFA_REQUIRED_AFTER = new Date('2025-05-12')
export const MFA_REQUIRED = new Date() >= MFA_REQUIRED_AFTER

/** @type {CognitoState} */
const initialState = {
  user: {
    isLogged: false,
    mfaSettings: null,
  },
  challenge: {
    name: null,
    username: null,
    session: null,
    deviceKey: null,
    deviceGroupKey: null,
  },
  mfaSetup: {
    accessToken: null,
    idToken: null,
    refreshToken: null,
    expiresIn: null,
    tokenType: null,
  },
  loading: false,
  error: null,
}

export function reducer(state, action) {
  switch (action.type) {
    case actions.loading:
      state.loading = true
      state.error = null
      return
    case actions.error: {
      state.loading = false
      state.error = action.payload
      return
    }
    case actions.setUser: {
      const { isLogged } = action.payload
      state.loading = false
      state.error = null
      state.user = { isLogged }
      if (!isLogged) {
        state.user.mfaSettings = null
        state.challenge = { name: null, username: null, session: null }
        state.challenge.deviceKey = null
        state.challenge.deviceGroupKey = null
      }
      return
    }
    case actions.setMFASetupChallenge: {
      const { accessToken, idToken, refreshToken, expiresIn, tokenType } = action.payload
      state.loading = false
      state.error = null
      state.mfaSetup = {
        accessToken,
        idToken,
        refreshToken,
        expiresIn,
        tokenType,
      }
      state.challenge = {
        name: challengeTypes.MFA_SETUP,
      }
      return
    }
    case actions.setChallenge: {
      const { mfa_settings, ...challenge } = action.payload
      state.loading = false
      state.error = null
      state.challenge = { ...state.challenge, ...challenge }
      if (mfa_settings) {
        state.user.mfaSettings = mfa_settings
      }
      return
    }
    case actions.resetChallenge: {
      state.loading = false
      state.error = null
      state.challenge = { name: null, username: null, session: null }
      state.mfaSetup = {
        accessToken: null,
        idToken: null,
        refreshToken: null,
        expiresIn: null,
        tokenType: null,
      }
      return
    }
    case actions.setMFASettings:
      state.loading = false
      state.error = null
      state.user.mfaSettings = action.payload
      return
    default:
      return
  }
}

export const asyncActionHandlers = {
  [actions.authenticateAsync]: authenticateAsync,
  [actions.handleChangePasswordChallengeAsync]: handleSetPasswordChallengeAsync,
  [actions.handleMFAChallengeAsync]: handleMFAChallengeAsync,
  [actions.getMFASettingsAsync]: getMFASettingsAsync,
  [actions.updateMFASettingsAsync]: getMFASettingsAsync,
  [actions.setupMFATOTPAsync]: setupMFATOTPAsync,
  [actions.setupEmailOTPAsync]: setupEmailOTPAsync,
}

function handleMFAChallengeAsync({ dispatch, getState }) {
  return async (action) => {
    const { mfaToken, rememberDevice } = action.payload
    const {
      challenge: { username, session, name: mfaMethod },
    } = getState()
    dispatch({ type: actions.loading })
    const response = await authApi.respondToMFAChallenge({
      username,
      session,
      mfaToken,
      mfaMethod,
      rememberDevice,
    })

    if (response.error) {
      handleError(dispatch, response, false, mfaMethod)
      return { success: false }
    }

    const { IdToken, DeviceKey, DeviceGroupKey } = response.data
    if (isAccountInvalid(IdToken)) {
      handleError(dispatch, {
        error: response.error,
        message: 'You are not authorized to login. Please contact an admin.',
      })
      dispatch({ type: actions.resetChallenge })
      return { success: false }
    }

    setRememberedDevice({ username, deviceKey: DeviceKey, deviceGroupKey: DeviceGroupKey })

    return { success: true, data: response.data }
  }
}

function handleSetPasswordChallengeAsync({ dispatch, getState }) {
  return async (action) => {
    const { newPassword } = action.payload
    const {
      challenge: { session, username },
    } = getState()
    dispatch({ type: actions.setChallenge, payload: { password: newPassword } })
    dispatch({ type: actions.loading })
    const response = await authApi.respondToSetPasswordChallenge({
      username,
      session,
      newPassword,
    })

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    const { IdToken, DeviceKey, DeviceGroupKey } = response.data

    if (isAccountInvalid(IdToken)) {
      handleError(dispatch, {
        error: response.error,
        message: 'You are not authorized to login. Please contact an admin.',
      })
      return { success: false }
    }

    setRememberedDevice({ username, deviceKey: DeviceKey, deviceGroupKey: DeviceGroupKey })
    dispatch({ type: actions.setUser, payload: { isLogged: true } })
    return { success: true, data: response.data }
  }
}

function authenticateAsync({ dispatch }) {
  return async (action) => {
    const { username, password, mfaMethod, includeDevice = true } = action.payload
    dispatch({ type: actions.setChallenge, payload: { name: mfaMethod, username, password } })
    dispatch({ type: actions.loading })
    let device = {}
    if (includeDevice) {
      device = JSON.parse(localStorage.getItem(REMEMBERED_DEVICE_KEY)) || {}
      if (device.username !== username) {
        device = {}
      }
    }
    const response = await authApi.authenticateUser({ username, password, mfaMethod, ...device })

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    const { AccessToken, Challenge, ChallengeSession, DeviceKey, DeviceGroupKey, mfa_settings } =
      response.data
    if (AccessToken) {
      const { IdToken } = response.data

      if (isAccountInvalid(IdToken)) {
        handleError(dispatch, {
          error: response.error,
          message: 'You are not authorized to login. Please contact an admin.',
        })
        return { success: false }
      }

      setRememberedDevice({ username, deviceKey: DeviceKey, deviceGroupKey: DeviceGroupKey })

      return { success: true, data: response.data }
    } else if (Challenge) {
      dispatch({
        type: actions.setChallenge,
        payload: {
          name: Challenge,
          username,
          password,
          session: ChallengeSession,
          deviceKey: DeviceKey,
          deviceGroupKey: DeviceGroupKey,
          mfa_settings,
        },
      })
      return { success: false, hasChallenge: true, data: response.data }
    } else {
      handleError(dispatch, response)
      return { success: false }
    }
  }
}

function getMFASettingsAsync({ dispatch }) {
  return async (action) => {
    const { silent, accessToken } = action.payload
    dispatch({ type: actions.loading })
    const response = await authApi.getMFASettings(accessToken)

    if (response.error) {
      console.error('Failed to get MFA settings', response.error)
      handleError(dispatch, response, silent)
      return
    }

    dispatch({ type: actions.setMFASettings, payload: response.data })
  }
}

function updateMFASettingsAsync({ dispatch }) {
  return async (action) => {
    const { mfaType, status, preferred, accessToken } = action.payload
    dispatch({ type: actions.loading })
    const response = await authApi.updateMFASettings({ mfaType, status, preferred, accessToken })

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    await getMFASettingsAsync({ dispatch })({ payload: { accessToken } })

    return { success: true, data: response.data }
  }
}

function setupMFATOTPAsync({ dispatch }) {
  return async (action) => {
    const { otp, accessToken } = action.payload
    dispatch({ type: actions.loading })
    const response = await authApi.verifyMFATOTP(otp, accessToken)

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    if (response.data !== true) {
      dispatch({ type: actions.error, payload: 'common.serverErrors.error.auth.mfa.wrong_token' })
      return { success: false }
    }

    const settings = await updateMFASettingsAsync({ dispatch })({
      payload: {
        mfaType: mfaTypes.softwareTokenMfa,
        status: 'true',
        preferred: 'true',
        accessToken,
      },
    })
    // also make sure Email OTP is setup whenever TOTP is setup
    await updateMFASettingsAsync({ dispatch })({
      payload: { mfaType: mfaTypes.emailTotp, status: 'true', preferred: 'false', accessToken },
    })
    if (settings.error) {
      handleError(dispatch, settings)
      return { success: false }
    }

    return { success: true, data: response.data }
  }
}

function setupEmailOTPAsync({ dispatch }) {
  return async (action) => {
    const { preferred, accessToken } = action.payload
    dispatch({ type: actions.loading })
    return await updateMFASettingsAsync({ dispatch })({
      payload: { mfaType: mfaTypes.emailTotp, status: true, preferred, accessToken },
    })
  }
}

export default function CognitoContextProvider({ children }) {
  const user = useSelector((state) => state.user)
  const isLogged = user.isLogged
  const [state, dispatch] = useImmerReducerAsync(
    reducer,
    {
      ...initialState,
      user: { ...initialState.user, isLogged },
    },
    asyncActionHandlers,
  )
  const reduxDispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const requiresMFA = useCallback(() => {
    if (!user.isLogged) {
      return undefined
    }
    if (!state.user.mfaSettings) {
      return undefined
    }

    return MFA_REQUIRED && !state.user.mfaSettings.required
  }, [user.isLogged, state.user.mfaSettings])

  const setAppUser = useCallback(
    (data, forceMfaSetup = false) => {
      let { AccessToken, IdToken, ExpiresIn, RefreshToken, TokenType, mfa_settings, DeviceKey, DeviceGroupKey } =
        data

      const userData = jwtDecode(IdToken)
      if (DeviceKey && DeviceGroupKey) {
        setRememberedDevice({ username: userData.email, deviceKey: DeviceKey, deviceGroupKey: DeviceGroupKey })
      }
      mfa_settings = mfa_settings || state.user.mfaSettings
      if (forceMfaSetup || (MFA_REQUIRED && !mfa_settings?.required)) {
        dispatch({
          type: actions.setMFASetupChallenge,
          payload: {
            accessToken: AccessToken,
            idToken: IdToken,
            refreshToken: RefreshToken,
            expiresIn: ExpiresIn,
            tokenType: TokenType,
          },
        })
        return
      }
      const cognitoGroups = userData['cognito:groups'] ?? []
      const testGroupsArr = [...cognitoGroups]
      const accessLevel = testGroupsArr.find((group) => group.includes('webapp-access-group'))

      reduxDispatch({
        type: LOGIN,
        payload: {
          id: userData.sub,
          tokenType: TokenType,
          accessToken: AccessToken,
          idToken: IdToken,
          refreshToken: RefreshToken,
          expiresInSec: ExpiresIn,
          name: userData.name,
          email: userData.email,
          email_verified: userData.email_verified,
          accessLevel: accessLevel || 'webapp-access-group-1',
          isAdmin: cognitoGroups && cognitoGroups.includes('fd-admin'),
          isBackOfficeAdmin: cognitoGroups && cognitoGroups.includes('back-office-admin'),
          cognitoGroups: cognitoGroups && cognitoGroups.filter((group) => group.match(/(partner-id-.+|fd\d+)/)),
          multi_partner_view: userData['custom:multi_partner_view'] ?? 'name',
        },
      })
      dispatch({ type: actions.setUser, payload: { isLogged: true } })
      dispatch({ type: actions.resetChallenge })
    },
    [reduxDispatch, dispatch, state.user],
  )

  const promoteTemporarySession = useCallback(() => {
    const { accessToken, idToken, refreshToken, expiresIn, tokenType } = state.mfaSetup
    if (!accessToken) {
      console.error('No access token found in mfaSetup')
      return
    }
    const userData = jwtDecode(idToken)
    const testGroupsArr = [...userData['cognito:groups']]
    const accessLevel = testGroupsArr.find((group) => group.includes('webapp-access-group'))
    const cognitoGroups = userData['cognito:groups'] ?? []
    const isAdmin = cognitoGroups && cognitoGroups.includes('fd-admin')
    reduxDispatch({
      type: LOGIN,
      payload: {
        id: userData.sub,
        tokenType,
        accessToken: accessToken,
        idToken: idToken,
        refreshToken,
        expiresInSec: expiresIn,
        name: userData.name,
        email: userData.email,
        email_verified: userData.email_verified,
        accessLevel: accessLevel || 'webapp-access-group-1',
        isAdmin,
        isBackOfficeAdmin: cognitoGroups && cognitoGroups.includes('back-office-admin'),
        multi_partner_view: userData['custom:multi_partner_view'] ?? 'name',
        cognitoGroups: cognitoGroups && cognitoGroups.filter((group) => group.match(/(partner-id-.+|fd\d+)/)),
      },
    })
    dispatch({ type: actions.setUser, payload: { isLogged: true } })
  }, [reduxDispatch, state.mfaSetup, dispatch])

  const authenticate = useCallback(
    ({ username, password, mfaMethod }, callback = () => { }) => {
      const action = { type: actions.authenticateAsync, payload: { username, password, mfaMethod } }
      authenticateAsync({ dispatch })(action).then(({ success, hasChallenge, data }) => {
        callback({ success, hasChallenge, data })
        if (success === false) {
          return
        }

        setAppUser(data)
      })
    },
    [dispatch, setAppUser],
  )
  const handleChangePasswordChallenge = useCallback(
    ({ newPassword }, callback = () => { }) => {
      const action = { type: actions.handleChangePasswordChallengeAsync, payload: { newPassword } }
      handleSetPasswordChallengeAsync({ dispatch, getState: () => state })(action).then(
        ({ success, data }) => {
          if (success) {
            setAppUser(data, true)
          }
          callback({ success, data })
        },
      )
    },
    [dispatch, state, setAppUser],
  )
  const handleMFAChallenge = useCallback(
    ({ mfaToken, rememberDevice }) => {
      const action = {
        type: actions.handleMFAChallenge,
        payload: { mfaToken, rememberDevice },
      }
      handleMFAChallengeAsync({ dispatch, getState: () => state })(action).then(
        ({ success, data }) => {
          if (!success) {
            return
          }

          setAppUser(data)
        },
      )
    },
    [dispatch, setAppUser, state],
  )
  const getMFASettings = useCallback(
    (silent = false, callback = () => { }) => {
      const action = {
        type: actions.getMFASettingsAsync,
        payload: { silent },
      }
      getMFASettingsAsync({ dispatch })(action).then(callback)
    },
    [dispatch],
  )
  const updateMFASettings = useCallback(
    (mfaType, status, preferred, callback = () => { }) => {
      const action = {
        type: actions.updateMFASettingsAsync,
        payload: { mfaType, status, preferred },
      }
      updateMFASettingsAsync({ dispatch })(action).then(callback)
    },
    [dispatch],
  )
  const removeSoftwareToken = useCallback(
    (callback = () => { }) => {
      updateMFASettings(mfaTypes.softwareTokenMfa, 'false', 'false', callback)
    },
    [updateMFASettings],
  )
  const setupMFATOTP = useCallback(
    (otp, callback = () => { }) => {
      const action = {
        type: actions.setupMFATOTPAsync,
        payload: { otp, accessToken: state.mfaSetup.accessToken },
      }
      setupMFATOTPAsync({ dispatch })(action).then(({ success }) => {
        callback({ success })
      })
    },
    [dispatch, state],
  )
  const setupEmailOTP = useCallback(
    (preferred, callback = () => { }) => {
      const action = {
        type: actions.setupEmailOTPAsync,
        payload: { preferred, accessToken: state.mfaSetup.accessToken },
      }
      setupEmailOTPAsync({ dispatch, getState: () => state })(action).then(({ success }) => {
        callback({ success })
      })
    },
    [dispatch, state],
  )
  const resendMFACodeViaEmail = useCallback(
    (callback = () => { }) => {
      const { challenge } = state
      if (!challenge.name || !challenge.username) {
        console.error('No challenge found to resend MFA code')
        return
      }
      const { username, password } = challenge
      authenticate({ username, password, mfaMethod: mfaTypes.emailTotp }, callback)
    },
    [authenticate, state],
  )

  const resetChallenge = useCallback(() => {
    dispatch({ type: actions.resetChallenge })
  }, [dispatch])

  const switchMFAMethod = useCallback(
    (mfaMethod, callback = () => { }) => {
      const {
        challenge: { name: challengeName, username, password },
      } = state
      if (mfaMethod === challengeName) {
        return
      }

      if (!challengeName || !username) {
        console.error('No challenge found to switch MFA method')
        return
      }

      authenticate(
        {
          username,
          password,
          mfaMethod,
        },
        callback,
      )
    },
    [state, authenticate],
  )

  const logout = useCallback(() => {
    dispatch({ type: actions.setUser, payload: { isLogged: false } })
    reduxDispatch({ type: LOGOUT })
  }, [dispatch, reduxDispatch])

  const setError = useCallback(
    (error) => {
      dispatch({ type: actions.error, payload: error })
    },
    [dispatch],
  )

  const unsetRememberedDevice = useCallback(() => {
    setRememberedDevice({ username: null, deviceKey: null, deviceGroupKey: null })
  }, [])

  useEffect(() => {
    history.listen((location, action) => {
      if (action === 'POP' && location.pathname === '/login') {
        resetChallenge()
      }
    })
  }, [history, resetChallenge])

  useEffect(() => {
    if (!user.isLogged) {
      dispatch({ type: actions.setUser, payload: { isLogged: false } })
    }
  }, [user.isLogged, dispatch, reduxDispatch])

  useEffect(() => {
    if (user.isLogged) {
      getMFASettings(true)
    }
    // re-fetch once a partner becomes known: the first attempt right after login/MFA
    // often has no valid partner context yet and gets rejected by the backend, which
    // would otherwise leave mfaSettings (and requiresMFA()) stuck forever for the session
  }, [getMFASettings, user.isLogged, user.partnerId])

  useEffect(() => {
    const mfaRequired = requiresMFA()
    if (mfaRequired === undefined) {
      return
    }

    const excludedPaths = ['/setup-mfa', '/mfa', 'setpassword']
    if (requiresMFA() && !excludedPaths.includes(location.pathname)) {
      history.replace('/setup-mfa')
    }
  }, [requiresMFA, location.pathname, history])

  return (
    <CognitoContext.Provider value={state}>
      <CognitoDispatchContext.Provider
        value={{
          dispatch,
          authenticate,
          handleChangePasswordChallenge,
          handleMFAChallenge,
          getMFASettings,
          updateMFASettings,
          removeSoftwareToken,
          setupMFATOTP,
          setupEmailOTP,
          resendMFACodeViaEmail,
          switchMFAMethod,
          requiresMFA,
          logout,
          setError,
          resetChallenge,
          promoteTemporarySession,
          unsetRememberedDevice,
        }}
      >
        {children}
      </CognitoDispatchContext.Provider>
    </CognitoContext.Provider>
  )
}

CognitoContextProvider.propTypes = {
  children: PropTypes.node,
}

function handleError(dispatch, { error, status, message, data }, silent = false, mfaMethod = null) {
  console.log('Cognito api failed', error, status, message, data)
  // eslint-disable-next-line eqeqeq
  if (status == 500) {
    setRememberedDevice({ username: null, deviceKey: null, deviceGroupKey: null })
    dispatch({ type: actions.error, payload: 'common.errors.inServerResponse' })
  }

  if (silent) {
    dispatch({ type: actions.error, payload: '' })
    return
  }

  if (message) {
    return dispatch({ type: actions.error, payload: message })
  }

  let errorCode = null
  const errorMsg = message ?? error?.response?.data?.message
  if (errorMsg && isErrorCode(errorMsg) && errorMsg.includes('wrong_token')) {
    errorCode = 'common.serverErrors.error.auth.mfa.wrong_token'
  } else {
    errorCode = codifyAPIError({ error, status, message, data })

    if (errorCode?.includes('session_expired') && mfaMethod) {
      errorCode = `common.serverErrors.error.auth.${mfaMethod.toLowerCase()}.session_expired`
    }
  }

  dispatch({ type: actions.error, payload: errorCode })
}

export function useCognito() {
  const context = React.useContext(CognitoContext)
  if (context === undefined) {
    throw new Error('useCognito must be used within a CognitoContextProvider')
  }
  return context
}

export function useCognitoDispatch() {
  const context = React.useContext(CognitoDispatchContext)
  if (context === undefined) {
    throw new Error('useCognitoDispatch must be used within a CognitoContextProvider')
  }
  return context
}

function setRememberedDevice({ username, deviceKey, deviceGroupKey }) {
  if (!deviceKey || !deviceGroupKey) {
    localStorage.removeItem(REMEMBERED_DEVICE_KEY)
    return
  }

  const device = { username, deviceKey, deviceGroupKey }
  localStorage.setItem(REMEMBERED_DEVICE_KEY, JSON.stringify(device))
}

function isAccountInvalid(idToken) {
  const userData = jwtDecode(idToken)
  const testGroupsArr = userData['cognito:groups'] ? [...userData['cognito:groups']] : []
  const isDisabledAccount = testGroupsArr.some((group) => group.includes('account-disabled'))
  const isExternalUser = testGroupsArr.some((group) => group.includes('external-user'))
  return isDisabledAccount || isExternalUser
}
